import { InitOptions } from 'i18next';
import type { WebAppName } from './types/WebAppName';

export const DEFAULT_LANGUAGE = 'en';

export const i18nConfig: InitOptions = {
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  // Use old plural handling
  // Reference: https://www.i18next.com/misc/migration-guide#v20.x.x-to-v21.0.0
  compatibilityJSON: 'v3',
  // i18next uses natural natural language selection after v21 which breaks our current ability to access keys with spaces.
  // To guard against this breaking change, we now manually set `nsSeparator` and `keySeparator`.
  // Reference https://www.i18next.com/misc/migration-guide#natural-language-detection
  nsSeparator: ':',
  keySeparator: false,
  debug: false,
};

export const GTV_BRAND_DISPLAY_NAMES = {
  candyjar: 'CandyJar',
  ironblood: 'Ironblood',
} as const;

export type GtvBrandId = keyof typeof GTV_BRAND_DISPLAY_NAMES;

export const DEFAULT_GTV_BRAND: GtvBrandId = 'candyjar';

export const isGtvBrandId = (brand: string | undefined): brand is GtvBrandId =>
  typeof brand === 'string' && Object.prototype.hasOwnProperty.call(GTV_BRAND_DISPLAY_NAMES, brand);

export const getGtvBrandId = (brand = process.env.NEXT_PUBLIC_BRAND): GtvBrandId =>
  isGtvBrandId(brand) ? brand : DEFAULT_GTV_BRAND;

const GTV_APP_NAME_TRANSLATION_KEYS: Record<string, string> = {
  'discovery:promo_banner_subtitle': 'discovery:promo_banner_subtitle_app_name',
};

export const getGtvAppNameTranslationKey = (key: string, brand: GtvBrandId = getGtvBrandId()): string => {
  if (brand === DEFAULT_GTV_BRAND) {
    return key;
  }

  return GTV_APP_NAME_TRANSLATION_KEYS[key] ?? key;
};

const getWebAppDisplayName = (appName: WebAppName): string =>
  appName === 'galatea' ? 'Galatea' : GTV_BRAND_DISPLAY_NAMES[getGtvBrandId()];

/**
 * Default interpolation so any key can reference the running brand as `{{appName}}` without
 * per-call variables (per-call values still win). For galateatv the display name follows the
 * brand build: NEXT_PUBLIC_BRAND is inlined client-side and runtime-readable server-side, set
 * only by brand deployments (ironblood-web) — absent everywhere else, so CandyJar is the default.
 */
export const brandInterpolation = (appName: WebAppName): InitOptions['interpolation'] => ({
  defaultVariables: {
    appName: getWebAppDisplayName(appName),
  },
});
